// API Configuration
export const API_CONFIG = {
  // Environment-based API URLs
  BASE_URL: getApiBaseUrl(),
  ENDPOINTS: {
    ITEMS: '/items'
  }
};

// Function to get the correct API base URL based on environment
function getApiBaseUrl() {
  const environment = process.env.NODE_ENV;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  
  // Custom environment variable override (highest priority)
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Production environment
  if (environment === 'production' || hostname === 'onboarding-form-app.juniper.net') {
    return 'https://jm6n6ywvpg.execute-api.us-west-2.amazonaws.com';
  }
  
  // Development/Local environment
  if (environment === 'development' || hostname === 'localhost') {
    return process.env.REACT_APP_DEV_API_BASE_URL || 'https://monl0hr4l1.execute-api.us-west-2.amazonaws.com';
  }
  
  // Default fallback
  return 'https://monl0hr4l1.execute-api.us-west-2.amazonaws.com';
}

// Security Configuration for CORS and API requests
export const SECURITY_CONFIG = {
  // CORS configuration
  CORS: {
    // Allowed origins for development vs production
    ALLOWED_ORIGINS: {
      development: ['http://localhost:3000', 'http://127.0.0.1:3000'],
      production: ['https://onboarding-form-app.juniper.net']
    },
    // Common CORS headers to include in requests
    HEADERS: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
      'Access-Control-Allow-Headers': 'Content-Type, Authorization, X-Requested-With'
    }
  },
  
  // API Request configuration
  API: {
    // Default headers for all API requests
    DEFAULT_HEADERS: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    
    // Request timeout
    TIMEOUT: 30000, // 30 seconds
    
    // Retry configuration for failed requests
    RETRY: {
      MAX_ATTEMPTS: 3,
      DELAY: 1000 // 1 second
    }
  },
  
  // Environment-specific configurations
  ENVIRONMENT: {
    IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
    IS_PRODUCTION: process.env.NODE_ENV === 'production',
    CURRENT_ORIGIN: typeof window !== 'undefined' ? window.location.origin : '',
    CURRENT_HOSTNAME: typeof window !== 'undefined' ? window.location.hostname : ''
  }
};

// Get full API URL
export const getApiUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Get security headers based on environment
export const getSecurityHeaders = (additionalHeaders = {}) => {
  const environment = SECURITY_CONFIG.ENVIRONMENT.IS_DEVELOPMENT ? 'development' : 'production';
  const currentOrigin = SECURITY_CONFIG.ENVIRONMENT.CURRENT_ORIGIN;
  
  return {
    ...SECURITY_CONFIG.API.DEFAULT_HEADERS,
    'Origin': currentOrigin,
    'Referer': currentOrigin,
    ...additionalHeaders
  };
};

// Debug configuration - logs current API configuration
export const debugApiConfig = () => {
  if (SECURITY_CONFIG.ENVIRONMENT.IS_DEVELOPMENT) {
    console.group('🔧 API Configuration Debug');
    console.log('Environment:', process.env.NODE_ENV);
    console.log('Hostname:', SECURITY_CONFIG.ENVIRONMENT.CURRENT_HOSTNAME);
    console.log('Origin:', SECURITY_CONFIG.ENVIRONMENT.CURRENT_ORIGIN);
    console.log('Base URL:', API_CONFIG.BASE_URL);
    console.log('Items Endpoint:', getApiUrl(API_CONFIG.ENDPOINTS.ITEMS));
    console.log('Security Headers:', getSecurityHeaders());
    console.groupEnd();
  }
};

// CORS error handler
export const handleCorsError = (error, url) => {
  const corsErrorMessages = [
    'CORS',
    'Cross-Origin Request Blocked',
    'Access to fetch',
    'No \'Access-Control-Allow-Origin\' header'
  ];
  
  const isCorsError = corsErrorMessages.some(msg => 
    error.message && error.message.includes(msg)
  );
  
  if (isCorsError) {
    console.error('🚫 CORS Error Detected:', {
      url,
      error: error.message,
      currentOrigin: SECURITY_CONFIG.ENVIRONMENT.CURRENT_ORIGIN,
      environment: process.env.NODE_ENV,
      suggestion: 'The API server needs to be configured with CORS headers that allow requests from this origin.'
    });
    
    return {
      isCorsError: true,
      userMessage: `API access blocked due to CORS policy. Current origin (${SECURITY_CONFIG.ENVIRONMENT.CURRENT_ORIGIN}) is not allowed by the server.`,
      technicalMessage: error.message
    };
  }
  
  return {
    isCorsError: false,
    userMessage: error.message,
    technicalMessage: error.message
  };
};
